import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  Card, Icon, Radio, Row, Statistic, Tag, Typography, Col, Progress, Spin,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import routeLocations from '../../../../utility/route-locations';
import {
  INSPECTION_LIST_TAG_COLOUR_COMPLIANCE, INSPECTION_LIST_TAG_COLOUR_OTHERS,
} from '../dashboard-util';
import { setLoadingParameterValues } from '../../../../state/ui/inspections/inspectionListUiActions';
import NoInspections from './NoInspections';
import {
  addDaysToJSDate,
  addYearsToJSDate,
  getDateComparisonNumber,
  getDateRangeForDashboardWidgetOption,
  isTimePeriodMovingUp
} from "../../bynx/packageUtil";

const { Text } = Typography;


function SummaryInspectionIssues({
  history, dashboardData, setLoadFilters, widgetSettings, updateWidgetNumbers, changeData, localLoading
}) {
  const intl = useIntl();

  // Inspection list filters if required
  const filterObj = {
    my: false,
    compliance: false,
    other: false,
    defects: false,
    noDefects: false,
    faults: false,
    damages: false,
    costs: false,
    dates: false,
  };

  // Display data and date range control
  const [data, setData] = useState([]);
  const [option, setOption] = useState(7);
  const [dates, setDates] = useState(null);

  // Option load
  useEffect(() => {
    if (widgetSettings && widgetSettings.length > 0) {
      setOption(widgetSettings[4]);
    }
  }, [widgetSettings, setOption]);

  // Load data
  useEffect(() => {
      // Data basis
      const baseData = {
        faults: 0,
        damages: 0,
        defectInspections: 0,
        inspections: 0,
        tagColour: '',
        tagTextId: '',
        filterKey: '',
      };

      // Today's date
      const todayDate = new Date();
      setData([]);
      const newData = [];

      // Do once for compliance and then others
      [...Array(2)].forEach((_v, index) => {
        // Compliance or Others
        const rowData = {
          ...baseData,
          tagColour: index === 0 ? INSPECTION_LIST_TAG_COLOUR_COMPLIANCE : INSPECTION_LIST_TAG_COLOUR_OTHERS,
          tagTextId: index === 0 ? 'compliance' : 'others',
          filterKey: index === 0 ? 'compliance' : 'other',
        };

        // Date Range...
        if (dashboardData && dashboardData.defectsByDate && dashboardData.defectsByDate.length > 0) {
          if (option === 1 || option === 2 || option === 4 || option === 7) {
            // 1 day or 1, 2, or 4 weeks
            let days = 1;
            if (option === 4) {
              days = 28;
            }
            if (option === 2) {
              days = 14;
            }
            if (option === 7) {
              days = 7;
            }
            const aDate = addDaysToJSDate(todayDate, (-days + 1)) // including today
            // eslint-disable-next-line max-len
            dashboardData.defectsByDate.filter(record => (record.complianceIndicator === (index === 0))).filter(row => {
              let loopQueryDate = row.submittedQueryDate;
              return loopQueryDate >= getDateComparisonNumber(aDate);
            }).forEach(row => {
              rowData.inspections += 1;
              rowData.defectInspections += (row.withChecklistFaults === 1 || row.withNewDamages === 1 ? 1 : 0);
              rowData.faults += row.withChecklistFaults;
              rowData.damages += row.withNewDamages;
            });
            setDates([moment(aDate).valueOf(), moment(todayDate).valueOf()]);
          } else if (option === 12) {
            // 12 months (all)
            let aDate = addYearsToJSDate(todayDate, -1);
            aDate = addDaysToJSDate(aDate, 1);
            // eslint-disable-next-line max-len
            dashboardData.defectsByDate.filter(record => (record.complianceIndicator === (index === 0))).filter(row => {
              let loopQueryDate = row.submittedQueryDate;
              return loopQueryDate >= getDateComparisonNumber(aDate);
            }).forEach(row => {
              rowData.inspections += 1;
              rowData.defectInspections += (row.withChecklistFaults === 1 || row.withNewDamages === 1 ? 1 : 0);
              rowData.faults += row.withChecklistFaults;
              rowData.damages += row.withNewDamages;
            });
            setDates([moment(todayDate).subtract(1, 'years').add(1, 'days').valueOf(), moment(todayDate).valueOf()]);
          }
        }
        newData.push(rowData);
      });
      setData(newData);
  }, [dashboardData, option]);

  // Change option and data
  const changeToNewOption = (newOption) => {
    // Must get data if moving up (already have if moving down)
    if (isTimePeriodMovingUp(option, newOption)) {
      const dateRange = getDateRangeForDashboardWidgetOption(newOption);
      changeData(dateRange[0], dateRange[1]);  //localLoading will be true during this
    }
    // Set new option
    setOption(newOption);
  };

  // No inspections (show dummy graph plus message)
  const hasInspections = dashboardData && dashboardData.inspectionStatistics && dashboardData.inspectionStatistics.totalInspections > 0;
  if (!hasInspections) return <NoInspections />;

  // Progress circle
  const ProgressCircle = ({ percentage, defects, filterKey }) => (
    <Progress
      percent={percentage}
      type="circle"
      width={90}
      strokeColor="var(--via-dashboard-defects-text-colour)"
      status="normal"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        setLoadFilters({
          ...filterObj,
          [filterKey]: true,
          defects,
          dates,
        });
        history.push(routeLocations.inspectionList);
      }}
    />
  );
  ProgressCircle.propTypes = {
    defects: PropTypes.bool,
    filterKey: PropTypes.string.isRequired,
    percentage: PropTypes.number.isRequired,
  };
  ProgressCircle.defaultProps = {
    defects: true,
  };

  // Inspection issues
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <Spin spinning={localLoading}>
      <Row type="flex" align="top" justify="center" style={{ height: '295px' }}>
        {data.map((row) => (
          <Card key={Math.random()} className="via-dashboard-card">
            <div className="via-dashboard-card-content" style={{ padding: '0.1em', height: '128px' }}>
              <Row type="flex" align="middle" justify="center" style={{ marginTop: '0.5em', marginLeft: '0.3em' }}>
                <Tag
                  color={row.tagColour}
                  onClick={() => {
                    setLoadFilters({
                      ...filterObj,
                      [row.filterKey]: true,
                      dates,
                    });
                    history.push(routeLocations.inspectionList);
                  }}
                  style={{ cursor: 'pointer', width: '150px', textAlign: 'center' }}
                >
                  <FormattedMessage id={row.tagTextId} />
                </Tag>
              </Row>
              {row.inspections > 0 && row.defectInspections > 0
                ? (
                  <Row type="flex" justify="space-between">
                    <Col style={{ width: '30%' }}>
                      <ProgressCircle filterKey={row.filterKey} percentage={Math.round((row.defectInspections / row.inspections) * 100)} />
                    </Col>
                    <Col style={{ width: '70%' }}>
                      <Row
                        type="flex"
                        justify="center"
                        align="bottom"
                        style={{ marginTop: '0.3em', marginBottom: '0.2em' }}
                      >
                        <Row onClick={() => {
                          setLoadFilters({
                            ...filterObj,
                            [row.filterKey]: true,
                            defects: true,
                            dates,
                          });
                          history.push(routeLocations.inspectionList);
                        }}
                        >
                          <Statistic
                            className="via-statistic"
                            value={row.defectInspections}
                            suffix={`/ ${row.inspections}`}
                            style={{ cursor: 'pointer' }}
                          />
                        </Row>
                        <Text
                          style={{
                            fontSize: '9pt',
                            marginLeft: '0.5em',
                            marginBottom: '0.4em',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            setLoadFilters({
                              ...filterObj,
                              [row.filterKey]: true,
                              defects: true,
                              dates,
                            });
                            history.push(routeLocations.inspectionList);
                          }}
                        >
                          <FormattedMessage
                            id="widgetIssuesDefects"
                            values={{ endS: row.inspections < 2 ? '' : intl.formatMessage({ id: 'pluralLetter' }) }}
                          />
                        </Text>
                      </Row>
                      <Row
                        type="flex"
                        justify="center"
                        align="middle"
                        onClick={() => {
                          setLoadFilters({
                            ...filterObj,
                            [row.filterKey]: true,
                            faults: true,
                            dates,
                          });
                          history.push(routeLocations.inspectionList);
                        }}
                        style={{
                          display: row.faults < 1 ? 'none' : '',
                          fontSize: '10pt',
                          color: 'var(--via-dashboard-checklist-failure-text-colour)',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon type="close-circle" />
                        &nbsp;
                        <Text style={{ fontSize: '10pt', color: 'var(--via-dashboard-checklist-failure-text-colour)' }}>
                          <b>{row.faults}</b>
                          &nbsp;
                          <FormattedMessage id="widgetOptionsChecklist" />
                        </Text>
                      </Row>
                      <Row
                        type="flex"
                        justify="center"
                        align="middle"
                        onClick={() => {
                          setLoadFilters({
                            ...filterObj,
                            [row.filterKey]: true,
                            damages: true,
                            dates,
                          });
                          history.push(routeLocations.inspectionList);
                        }}
                        style={{
                          display: row.damages < 1 ? 'none' : '',
                          fontSize: '10pt',
                          color: 'var(--via-dashboard-new-damages-text-colour)',
                          marginTop: '0.5em',
                          marginBottom: '0.5em',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon type="exclamation-circle" />
                        &nbsp;
                        <Text style={{ fontSize: '10pt', color: 'var(--via-dashboard-new-damages-text-colour)' }}>
                          <b>{row.damages}</b>
                          &nbsp;
                          <FormattedMessage id="widgetOptionsDamages" />
                        </Text>
                      </Row>
                    </Col>
                  </Row>
                )
                : (
                  <Row type="flex" align="middle" justify="center">
                    <Col style={{ width: '30%' }}>
                      <ProgressCircle filterKey={row.filterKey} defects={false} percentage={0} />
                    </Col>
                    <Col style={{ width: '70%' }}>
                      <Row type="flex" align="middle" justify="start" style={{ marginLeft: '4em' }}>
                        <Icon type={row.inspections > 0 ? 'check-circle' : 'carry-out'} theme="twoTone" style={{ fontSize: '18pt' }} />
                        {row.inspections > 0
                          ? (
                            <Text
                              style={{
                                fontSize: '9pt',
                                marginLeft: '0.5em',
                                marginBottom: '0.4em',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setLoadFilters({
                                  ...filterObj,
                                  [row.filterKey]: true,
                                  dates,
                                });
                                history.push(routeLocations.inspectionList);
                              }}
                            >
                              <FormattedMessage
                                id="widgetIssuesAndNoDefects"
                                values={{
                                  count: row.inspections,
                                  endS: row.inspections < 2 ? '' : intl.formatMessage({ id: 'pluralLetter' }),
                                }}
                              />
                            </Text>
                          )
                          : <Text style={{ fontSize: '9pt', marginLeft: '0.5em' }}><FormattedMessage id="widgetMessageTitle" /></Text>}
                      </Row>
                    </Col>
                  </Row>
                )}
            </div>
          </Card>
        ))}
      </Row>
      <Row type="flex" justify="center" align="middle" style={{ flex: '0 0 auto', marginTop: '1.1em' }}>
        <Radio.Group
          size="small"
          value={option}
          onChange={e => {
            changeToNewOption(e.target.value);
            updateWidgetNumbers(widgetSettings[0], widgetSettings[1], widgetSettings[2], widgetSettings[3], e.target.value);
          }}
        >
          <Radio.Button value={1}><FormattedMessage id="widgetOptionsDay" /></Radio.Button>
          <Radio.Button value={7}><FormattedMessage id="widgetOptionsDays" /></Radio.Button>
          <Radio.Button value={2}><FormattedMessage id="widgetOptionsFortnight" /></Radio.Button>
          <Radio.Button value={4}><FormattedMessage id="widgetOptionsWeeks" /></Radio.Button>
          <Radio.Button value={12}><FormattedMessage id="widgetOptionsYear" /></Radio.Button>
        </Radio.Group>
      </Row>
      </Spin>
    </div>
  );
}

SummaryInspectionIssues.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  history: PropTypes.oneOfType([PropTypes.object]),
  setLoadFilters: PropTypes.func.isRequired,
  changeData: PropTypes.func.isRequired,
  localLoading: PropTypes.bool.isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

SummaryInspectionIssues.defaultProps = {
  dashboardData: null,
  history: null,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};


export default function DashTableIssues({
  history, dashboardData, widgetSettings, updateWidgetNumbers, changeData, localLoading
}) {
  const dispatch = useDispatch();
  const setLoadFilters = paramObj => dispatch(setLoadingParameterValues(paramObj));

  return (
    <SummaryInspectionIssues
      history={history}
      dashboardData={dashboardData}
      setLoadFilters={setLoadFilters}
      changeData={changeData}
      localLoading={localLoading}
      widgetSettings={widgetSettings}
      updateWidgetNumbers={updateWidgetNumbers}
    />
  );
}

DashTableIssues.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  history: PropTypes.oneOfType([PropTypes.object]),
  changeData: PropTypes.func.isRequired,
  localLoading: PropTypes.bool.isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

DashTableIssues.defaultProps = {
  dashboardData: null,
  history: null,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};
